import axios from "axios";

import { apiClient } from "~/services/apiClient";
import type {
  CreateUserPayload,
  OperatorPresenceSnapshot,
  UpdateUserPayload,
  UserRecord,
} from "~/types/user";
import type {
  UserExportRequest,
  UserExportResult,
} from "~/types/export";
import type { SearchResponse } from "~/types/search";
import { toSearchMeta } from "~/types/search";

/**
 * Backend TODO (AC-66 / AC-239):
 * Add operator export API contract and wire it here once available.
 *
 * Proposed endpoint:
 * - POST /api/users/export
 *
 * Proposed request payload:
 * - scope: "all" | "filtered" | "selected"
 * - format: "csv" | "pdf"
 * - selectedIds?: string[]
 * - filters?: unknown[]
 * - search?: string
 * - columns?: string[]
 *
 * Expected backend behavior:
 * - Exclude SIP password fields from all exports.
 * - Small exports: immediate file response.
 * - Large exports: queue background job + send email notification when ready.
 * - Audit export completion with user, timestamp, format, record count.
 */
const UNSUPPORTED_ROUTE_STATUSES = new Set([404, 405, 501]);

function isUnsupportedRouteError(error: unknown) {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  return typeof status === "number" && UNSUPPORTED_ROUTE_STATUSES.has(status);
}
export async function getUsers() {
  const response = await apiClient.get<{ data: UserRecord[] }>("/api/users");
  return response.data.data;
}

export type UserSearchParams = {
  search?: string;
  page?: number;
  limit?: number;
  /** Serialized table filters, see ~/utils/tableFilters. */
  filters?: string;
};

export async function searchUsers(
  params: UserSearchParams = {}
): Promise<SearchResponse<UserRecord>> {
  const response = await apiClient.get<{
    data?: UserRecord[];
    meta?: unknown;
  }>("/api/users", { params });
  const data = Array.isArray(response.data?.data) ? response.data.data : [];
  return {
    data,
    meta: toSearchMeta(response.data?.meta, {
      query: params.search ?? "",
      page: params.page ?? 1,
      limit: params.limit ?? 10,
      totalCount: data.length,
      resultCount: data.length,
      totalPages: data.length > 0 ? 1 : 0,
    }),
  };
}

/**
 * Values an operators list filter can offer, taken from every operator rather than the page on
 * screen.
 */
export async function getUserFilterOptions(field: string): Promise<string[]> {
  const response = await apiClient.get<{ data?: unknown }>(
    "/api/users/filter-options",
    { params: { field } }
  );
  const data = response.data?.data;
  return Array.isArray(data)
    ? data.filter((value): value is string => typeof value === "string")
    : [];
}

export async function getUserById(id: string) {
  const response = await apiClient.get<UserRecord>(`/api/users/${id}`);
  return response.data;
}

export async function createUser(payload: CreateUserPayload) {
  const response = await apiClient.post<UserRecord>("/api/users", payload);
  return response.data;
}

export async function updateUser(id: string, payload: UpdateUserPayload) {
  const response = await apiClient.put<UserRecord>(`/api/users/${id}`, payload);
  return response.data;
}

export async function patchUser(id: string, payload: UpdateUserPayload) {
  const response = await apiClient.patch<UserRecord>(`/api/users/${id}`, payload);
  return response.data;
}

export async function deleteUser(id: string) {
  await apiClient.delete(`/api/users/${id}`);
}

export async function deactivateUser(id: string) {
  try {
    const response = await apiClient.patch<UserRecord>(`/api/users/${id}/deactivate`);
    return response.data;
  } catch (error) {
    if (!isUnsupportedRouteError(error)) {
      throw error;
    }
  }

  try {
    const response = await apiClient.patch<UserRecord>(`/api/users/${id}/soft-delete`);
    return response.data;
  } catch (error) {
    if (!isUnsupportedRouteError(error)) {
      throw error;
    }
  }

  const response = await apiClient.patch<UserRecord>(`/api/users/${id}`, {
    status: "INACTIVE",
  });
  return response.data;
}

export async function archiveUser(id: string) {
  try {
    await apiClient.delete(`/api/users/${id}/archive`);
    return;
  } catch (error) {
    if (!isUnsupportedRouteError(error)) {
      throw error;
    }
  }

  await deleteUser(id);
}

export async function setUserStatus(status: "Online" | "Offline") {
  await apiClient.patch("/api/users/status", { status });
}

/** Live availability derived from the sidecar — SIP registration plus dashboard session. */
export async function getOperatorPresence(): Promise<OperatorPresenceSnapshot> {
  const response = await apiClient.get<OperatorPresenceSnapshot>(
    "/api/users/presence"
  );
  return {
    generatedAt: response.data?.generatedAt ?? new Date().toISOString(),
    sidecarReachable: response.data?.sidecarReachable ?? false,
    sidecarError: response.data?.sidecarError ?? null,
    data: Array.isArray(response.data?.data) ? response.data.data : [],
  };
}

export async function exportUsers(
  payload: UserExportRequest
): Promise<UserExportResult> {
  const response = await apiClient.post("/api/users/export", payload, {
    responseType: "blob",
    validateStatus: (status) =>
      (status >= 200 && status < 300) || status === 202,
  });

  const contentType =
    typeof response.headers["content-type"] === "string"
      ? response.headers["content-type"]
      : "";

  if (contentType.includes("text/csv")) {
    const blob = response.data as Blob;
    const fallbackFilename = "users-export.csv";
    const contentDisposition = response.headers[
      "content-disposition"
    ] as string | undefined;
    let filename = fallbackFilename;
    if (typeof contentDisposition === "string") {
      const match = /filename="?([^"]+)"?/i.exec(contentDisposition);
      if (match?.[1]) {
        filename = match[1];
      }
    }

    return {
      kind: "file",
      blob,
      filename,
    };
  }

  if (response.status === 202) {
    return {
      kind: "job",
      job: response.data,
    } as UserExportResult;
  }

  return {
    kind: "user-json",
    payload: response.data,
  } as UserExportResult;
}

export async function checkUserEmail(email: string) {
  const response = await apiClient.get<{
    exists: boolean;
    user: {
      id: string;
      role: string;
      status: string;
    } | null;
  }>("/api/users/check-email", {
    params: { email },
  });

  return response.data;
}

export type ResendInviteResponse = {
  invitationEmailStatus: "SENT" | "FAILED" | string;
  message: string;
};

export async function resendUserInvite(id: string) {
  const response = await apiClient.post<ResendInviteResponse>(
    `/api/users/${id}/resend-invite`
  );
  return response.data;
}

export type SipSidecarCheckResult =
  | { ok: true; data: unknown }
  | { ok: false; error: string; status?: number };

export type UserSipCheckResponse = {
  ok: boolean;
  autoProvisioned?: boolean;
  sipCredential?: unknown;
  checks?: {
    sidecar?: SipSidecarCheckResult;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

export async function checkUserSip(
  id: string,
  options?: { autoProvision?: boolean }
) {
  const response = await apiClient.get<UserSipCheckResponse>(
    `/api/users/${id}/sip/check`,
    {
      params: options?.autoProvision ? { autoProvision: true } : undefined,
    }
  );
  return response.data;
}

export async function checkMySip(options?: { autoProvision?: boolean }) {
  const response = await apiClient.get<UserSipCheckResponse>(
    `/api/users/me/sip/check`,
    {
      params: options?.autoProvision ? { autoProvision: true } : undefined,
    }
  );
  return response.data;
}
